'use client';

import { useState, type FormEvent } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { klyuchOshibki, pochtaPohozha } from '@/lib/licenzii';

/**
 * «Потерял ключ» (#242): почта → письмо со всеми лицензиями.
 *
 * Ответ одинаковый для всех: «если лицензии есть, мы прислали письмо».
 * Иначе форма отвечала бы на вопрос «покупал ли что-то владелец этой
 * почты» любому, кто её знает. Отличается только лимит частоты: о нём
 * надо сказать, иначе человек ждёт письма, которое не уходило.
 */
export function PoteryalKlyuch() {
  const t = useTranslations('ais_licenzii');
  const locale = useLocale();
  const [pochta, setPochta] = useState('');
  const [idyot, setIdyot] = useState(false);
  const [gotovo, setGotovo] = useState(false);
  const [oshibka, setOshibka] = useState('');

  async function otpravit(e: FormEvent) {
    e.preventDefault();
    setOshibka('');
    if (!pochtaPohozha(pochta)) {
      setOshibka(t('proverka_pochta'));
      return;
    }
    setIdyot(true);
    try {
      const r = await fetch('/api/licenses/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pochta.trim(), locale: locale === 'ru' ? 'ru' : 'en' }),
      });
      if (r.ok) {
        setGotovo(true);
        return;
      }
      setOshibka(t(`oshibki.${klyuchOshibki(r.status, await r.json().catch(() => null))}`));
    } catch {
      setOshibka(t('oshibki.obshchaya'));
    } finally {
      setIdyot(false);
    }
  }

  if (gotovo) {
    return (
      <p
        className="p-6 rounded-2xl bg-[var(--color-bg-card)] border border-[var(--color-border)]"
        role="status"
      >
        {t('l_gotovo')}
      </p>
    );
  }

  return (
    <form
      onSubmit={(e) => void otpravit(e)}
      noValidate
      className="flex flex-col gap-4 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-7"
    >
      <label className="flex flex-col gap-1.5 text-sm">
        <span className="font-medium">{t('pochta')}</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={pochta}
          onChange={(e) => {
            setPochta(e.target.value);
          }}
          className="px-4 py-3 rounded-xl bg-[var(--color-bg)] border border-[var(--color-border)] focus:border-[var(--color-accent-500)] outline-none"
        />
      </label>
      {oshibka !== '' && (
        <p className="text-sm text-[var(--color-danger-400)]" role="alert">
          {oshibka}
        </p>
      )}
      <button
        type="submit"
        disabled={idyot}
        className="self-start px-6 py-3 rounded-xl font-semibold text-sm bg-[var(--color-accent-500)] hover:bg-[var(--color-accent-400)] text-white disabled:opacity-60"
      >
        {idyot ? t('l_otpravlyaem') : t('l_otpravit')}
      </button>
    </form>
  );
}
